import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from "react";

interface PatientTimerContextType {
  expiredPatientIds: Set<string>;
  expiredPatientIdsArray: string[];
  addExpiredPatient: (patientId: string) => void;
  removeExpiredPatient: (patientId: string) => void;
  clearExpiredPatients: () => void;
}

const PatientTimerContext = createContext<PatientTimerContextType | undefined>(
  undefined
);

export function PatientTimerProvider({ children }: { children: ReactNode }) {
  const [expiredPatientIdsSet, setExpiredPatientIdsSet] = useState<Set<string>>(
    new Set()
  );
  const [updateTrigger, setUpdateTrigger] = useState(0);

  const expiredPatientIdsArray = useMemo(() => {
    return Array.from(expiredPatientIdsSet);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiredPatientIdsSet, updateTrigger]);

  const addExpiredPatient = useCallback((patientId: string) => {
    setExpiredPatientIdsSet((prev) => {
      if (prev.has(patientId)) return prev;
      const next = new Set(prev);
      next.add(patientId);
      setUpdateTrigger((t) => t + 1);
      return next;
    });
  }, []);

  const removeExpiredPatient = useCallback((patientId: string) => {
    setExpiredPatientIdsSet((prev) => {
      if (!prev.has(patientId)) return prev;
      const next = new Set(prev);
      next.delete(patientId);
      setUpdateTrigger((t) => t + 1);
      return next;
    });
  }, []);

  const clearExpiredPatients = useCallback(() => {
    setExpiredPatientIdsSet(new Set());
    setUpdateTrigger((t) => t + 1);
  }, []);

  return (
    <PatientTimerContext.Provider
      value={{
        expiredPatientIds: expiredPatientIdsSet,
        expiredPatientIdsArray,
        addExpiredPatient,
        removeExpiredPatient,
        clearExpiredPatients,
      }}
    >
      {children}
    </PatientTimerContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function usePatientTimerContext() {
  const context = useContext(PatientTimerContext);
  if (context === undefined) {
    return {
      expiredPatientIds: new Set<string>(),
      expiredPatientIdsArray: [],
      addExpiredPatient: () => {},
      removeExpiredPatient: () => {},
      clearExpiredPatients: () => {},
    };
  }
  return context;
}
